import { Camera, Object3D, Scene, WebGLRenderer, WebGLRenderTarget } from 'three';
import { Text } from 'troika-three-text';

import { TextBatch } from '../elements/components/text-batch';
import { SunManager } from '../sun-manager';
import { PaintEntry, PaintPart } from './manifest';

/** The lists of a three.js render, in the order they're drawn, each sorted as three.js draws it. */
export type RenderLists = {
  opaque: { object: Object3D }[];
  transmissive: { object: Object3D }[];
  transparent: { object: Object3D }[];
};

type ObjectFlags = Partial<
  Record<'isPoints' | 'isLine' | 'isLineSegments2' | 'isLine2' | 'isMesh', boolean>
>;

/** The part of an asset the object draws, undefined for what isn't drawn in the report. */
export function getPaintPart(object: Object3D): PaintPart | undefined {
  if (object instanceof Text || object instanceof TextBatch) {
    return 'texts';
  }

  // Wide lines are meshes, so they're checked first.
  const flags = object as ObjectFlags;
  if (flags.isLine || flags.isLineSegments2 || flags.isLine2) {
    return 'lines';
  }
  if (flags.isPoints) {
    return 'points';
  }
  if (flags.isMesh) {
    return 'faces';
  }

  return undefined;
}

/**
 * Gets the order to paint the layers and their parts in, so what is on top in the viewer is on top
 * in the PDF: the first time each is drawn in the render lists. getLayerId gives the layer an
 * object belongs to, undefined if it's not in the report. Whole layers are painted at once, without
 * parts.
 */
export function getPaintOrder(
  lists: RenderLists,
  getLayerId: (object: Object3D) => string | undefined,
  wholeLayers: ReadonlySet<string> = new Set(),
): PaintEntry[] {
  const entries: PaintEntry[] = [];
  const added = new Set<string>();

  for (const { object } of [...lists.opaque, ...lists.transmissive, ...lists.transparent]) {
    const layer = getLayerId(object);
    const part = getPaintPart(object);
    if (layer === undefined || part === undefined || added.has(`${layer}/${part}`)) {
      continue;
    }

    if (wholeLayers.has(layer)) {
      if (!added.has(layer)) {
        added.add(layer);
        entries.push({ layer });
      }
      continue;
    }

    added.add(`${layer}/${part}`);
    entries.push({ layer, part });
  }

  return entries;
}

/**
 * Renders the scene with the camera, off screen and with the viewer's lights, and returns what
 * three.js drew in its order.
 */
function readRenderLists(renderer: WebGLRenderer, scene: Scene, camera: Camera): RenderLists {
  // The size doesn't matter, only the order things are drawn in.
  const target = new WebGLRenderTarget(16, 16);
  const previousTarget = renderer.getRenderTarget();

  try {
    renderer.setRenderTarget(target);
    SunManager.instance.renderObjectWithLightSources(renderer, scene, camera);

    // The list of a render that isn't inside another one.
    const list = renderer.renderLists.get(scene, 0);
    return {
      opaque: [...list.opaque],
      transmissive: [...list.transmissive],
      transparent: [...list.transparent],
    };
  } finally {
    renderer.setRenderTarget(previousTarget);
    target.dispose();
  }
}

/**
 * Exported as a collection to allow for mocking, as tests have no WebGL.
 */
export const paintOrderRenderer = { readRenderLists };
